import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { SPEECH_LANG } from "@/lib/dictation";
import { logError } from "@/lib/logError";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
export const LIVE_SUPPORTED = !!SR;
const CHUNK_MS = 4000;
const SILENCE = 0.02;

function useLiveEngine(lang, onFinal, setInterim, onError) {
  const rec = useRef(null);
  const start = () => {
    const r = new SR();
    r.lang = SPEECH_LANG[lang] || "fr-CA";
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const tr = e.results[i][0].transcript;
        if (e.results[i].isFinal) onFinal(tr); else interim += tr;
      }
      setInterim(interim);
    };
    r.onerror = (e) => { if (e.error !== "no-speech" && e.error !== "aborted") onError(e.error); };
    r.onend = () => { if (rec.current === r) { try { r.start(); } catch (e) { logError("Relance de la dictée")(e); } } };
    rec.current = r;
    r.start();
  };
  const stop = () => { const r = rec.current; rec.current = null; if (r) r.stop(); };
  return { start, stop };
}

function usePreciseEngine(lang, onFinal, setInterim, onError) {
  const st = useRef(null);
  const send = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "dictee.webm");
    fd.append("lang", lang);
    const r = await fetch(`${API}/dictee/transcribe`, { method: "POST", body: fd, credentials: "include" });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Transcription indisponible");
    return (await r.json()).text;
  };
  const cycle = (s) => {
    const mr = new MediaRecorder(s.stream);
    const parts = [];
    s.peak = 0;
    mr.ondataavailable = (e) => e.data.size && parts.push(e.data);
    mr.onstop = () => {
      const voiced = s.peak > SILENCE;
      if (voiced && parts.length) {
        s.pending += 1;
        setInterim("…");
        const blob = new Blob(parts, { type: mr.mimeType || "audio/webm" });
        s.queue = s.queue.then(() => send(blob)).then((t) => t && onFinal(t)).catch((e) => onError(e.message))
          .finally(() => { s.pending -= 1; if (!s.pending) setInterim(""); });
      }
      if (st.current === s) cycle(s);
    };
    mr.start();
    s.timer = setTimeout(() => mr.state === "recording" && mr.stop(), CHUNK_MS);
    s.mr = mr;
  };
  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const ctx = new AudioContext();
    const an = ctx.createAnalyser();
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Float32Array(an.fftSize);
    const s = { stream, ctx, queue: Promise.resolve(), pending: 0, peak: 0 };
    s.meter = setInterval(() => {
      an.getFloatTimeDomainData(buf);
      const rms = Math.sqrt(buf.reduce((a, v) => a + v * v, 0) / buf.length);
      s.peak = Math.max(s.peak, rms);
    }, 100);
    st.current = s;
    cycle(s);
  };
  const stop = () => {
    const s = st.current;
    st.current = null;
    if (!s) return;
    clearTimeout(s.timer);
    clearInterval(s.meter);
    if (s.mr?.state === "recording") s.mr.stop();
    s.stream.getTracks().forEach((t) => t.stop());
    s.ctx.close();
  };
  return { start, stop };
}

export function useDictation({ lang, mode, onFinal }) {
  const [active, setActive] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState("");
  const finalRef = useRef(onFinal);
  finalRef.current = onFinal;
  const emit = useCallback((t) => finalRef.current(t), []);
  const onError = useCallback((m) => setError(m === "not-allowed" ? "micro" : m), []);
  const live = useLiveEngine(lang, emit, setInterim, onError);
  const precise = usePreciseEngine(lang, emit, setInterim, onError);
  const engine = mode === "live" ? live : precise;
  const engineRef = useRef(engine);
  engineRef.current = engine;

  const stop = useCallback(() => { engineRef.current.stop(); setActive(false); setInterim(""); }, []);
  const start = async () => {
    setError("");
    try { await engine.start(); setActive(true); } catch (e) { setError(e?.name === "NotAllowedError" ? "micro" : e?.message || "micro"); }
  };
  useEffect(() => () => { engineRef.current.stop(); }, []);
  return { active, interim, error, start, stop };
}
